import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
  CircularProgress,
  OutlinedInput,
  InputAdornment,
  Popover,
  IconButton
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { preventNegativeInput, limitMaxTen } from 'src/utlis/form-validation';

const BasicDetailTab = ({
  form,
  handleInput,
  inputRef,
  handleLocationClick,
  loadingProperty,
  loadingStatus,
  statusOptions,
  typeOptions,
  areaUnitOptions,
  setField,
  states,
  cities,
  loadingStates,
  loadingCities,
  triggerAddressSearch,
  onDataSubmit,
  onStateChange
}) => {
  const [statesSearch, setStatesSearch] = useState('');
  const [citiesSearch, setCitiesSearch] = useState('');
  const [statesAnchorEl, setStatesAnchorEl] = useState(null);
  const [citiesAnchorEl, setCitiesAnchorEl] = useState(null);

  // Filter states based on search
  const filteredStates = useMemo(() => {
    if (!Array.isArray(states)) return [];
    if (!statesSearch.trim()) return states;
    return states.filter((state) => state.name.toLowerCase().includes(statesSearch.toLowerCase()));
  }, [states, statesSearch]);

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!Array.isArray(cities)) return [];
    if (!citiesSearch.trim()) return cities;
    return cities.filter((city) => city.name.toLowerCase().includes(citiesSearch.toLowerCase()));
  }, [cities, citiesSearch]);

  // State selection handler
  const handleStateSelect = (stateId) => {
    onStateChange({ target: { name: 'state', value: stateId } });
    setStatesAnchorEl(null);
    setStatesSearch('');

    // Clear city when state changes
    handleInput({ target: { name: 'city', value: '' } });
  };

  // City selection handler
  const handleCitySelect = (cityId) => {
    handleInput({ target: { name: 'city', value: cityId } });
    setCitiesAnchorEl(null);
    setCitiesSearch('');
  };

  // Open/Close popovers
  const handleStatesInputClick = (event) => {
    setStatesAnchorEl(event.currentTarget);
  };

  const handleCitiesInputClick = (event) => {
    if (form.state) {
      setCitiesAnchorEl(event.currentTarget);
    }
  };

  const handleCloseStates = () => {
    setStatesAnchorEl(null);
    setStatesSearch('');
  };

  const handleCloseCities = () => {
    setCitiesAnchorEl(null);
    setCitiesSearch('');
  };

  // Clear search inputs
  const handleClearStatesSearch = () => {
    setStatesSearch('');
  };

  const handleClearCitiesSearch = () => {
    setCitiesSearch('');
  };

  // Function to get the selected area unit name
  const getSelectedAreaUnitName = () => {
    if (!form.areaUnit) return 'Area Unit';
    const selectedUnit = areaUnitOptions.find((unit) => unit.name === form.areaUnit);
    return selectedUnit ? selectedUnit.name : 'Area Unit';
  };

  // Get display value for state and city
  const getSelectedStateName = () => {
    if (!form.state) return '';
    const selectedState = states.find((state) => state.id === form.state);
    return selectedState ? selectedState.name : '';
  };

  const getSelectedCityName = () => {
    if (!form.city) return '';
    const selectedCity = cities.find((city) => city.id === form.city);
    return selectedCity ? selectedCity.name : '';
  };

  return (
    <Box component="form" noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Title *"
            name="title"
            value={form.title}
            onChange={handleInput}
            placeholder="Property title"
            disabled={loadingProperty}
          />
        </Grid>

        <Grid item xs={12}>
          {/* <TextField
            fullWidth
            label="Address (Google Address) *"
            name="address"
            value={form.address}
            onChange={handleInput}
            placeholder="Start typing address..."
            inputRef={inputRef}
            disabled={loadingProperty}
          /> */}
          <TextField
            fullWidth
            label="Address "
            name="address"
            value={form.address}
            onChange={(e) => {
              handleInput(e);
              if (e.target.value && e.target.value.length > 3) {
                triggerAddressSearch(e.target.value);
              }
            }}
            inputRef={inputRef}
            required
            disabled={loadingProperty}
            helperText="Start typing and select from Google suggestions"
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            onClick={handleLocationClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#000080',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            <LocationOnIcon sx={{ marginRight: 1, fontSize: 24 }} />
            <Typography variant="body1" sx={{ fontWeight: '600', fontSize: '16px' }}>
              Set Location on map
            </Typography>
          </Box>
        </Grid>

        {/* State Dropdown with Search */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>State *</InputLabel>
            <OutlinedInput
              value={getSelectedStateName()}
              label="State *"
              readOnly
              onClick={handleStatesInputClick}
              disabled={loadingProperty || loadingStates}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={handleStatesInputClick}
                    disabled={loadingProperty || loadingStates}
                  >
                    {Boolean(statesAnchorEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  </IconButton>
                </InputAdornment>
              }
              sx={{ cursor: 'pointer' }}
            />
          </FormControl>

          {/* States Search Popover */}
          <Popover
            open={Boolean(statesAnchorEl)}
            anchorEl={statesAnchorEl}
            onClose={handleCloseStates}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            PaperProps={{
              sx: { width: 400, maxHeight: 400, mt: 1 }
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Search Input */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <OutlinedInput
                  placeholder="Search states..."
                  value={statesSearch}
                  onChange={(e) => setStatesSearch(e.target.value)}
                  size="small"
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                  endAdornment={
                    statesSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearStatesSearch}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                />
              </FormControl>

              {/* States List */}
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {loadingStates ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Loading states...</Typography>
                  </Box>
                ) : filteredStates.length > 0 ? (
                  <>
                    <MenuItem value="" onClick={() => handleStateSelect('')}>
                      <em>None</em>
                    </MenuItem>
                    {filteredStates.map((state) => (
                      <MenuItem
                        key={state.id}
                        value={state.id}
                        onClick={() => handleStateSelect(state.id)}
                      >
                        {state.name}
                      </MenuItem>
                    ))}
                  </>
                ) : (
                  <Typography sx={{ color: 'text.secondary', textAlign: 'center', p: 3 }}>
                    {statesSearch ? 'No states found' : 'No states available'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Popover>
        </Grid>

        {/* City Dropdown with Search */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>City *</InputLabel>
            <OutlinedInput
              value={getSelectedCityName()}
              label="City *"
              readOnly
              onClick={handleCitiesInputClick}
              disabled={loadingProperty || !form.state || loadingCities}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={handleCitiesInputClick}
                    disabled={loadingProperty || !form.state || loadingCities}
                  >
                    {Boolean(citiesAnchorEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  </IconButton>
                </InputAdornment>
              }
              sx={{
                cursor: form.state ? 'pointer' : 'not-allowed',
                opacity: form.state ? 1 : 0.7
              }}
            />
            {!form.state && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                Please select a state first
              </Typography>
            )}
          </FormControl>

          {/* Cities Search Popover */}
          <Popover
            open={Boolean(citiesAnchorEl)}
            anchorEl={citiesAnchorEl}
            onClose={handleCloseCities}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            PaperProps={{
              sx: { width: 400, maxHeight: 400, mt: 1 }
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Search Input */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <OutlinedInput
                  placeholder="Search cities..."
                  value={citiesSearch}
                  onChange={(e) => setCitiesSearch(e.target.value)}
                  size="small"
                  disabled={!form.state}
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                  endAdornment={
                    citiesSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearCitiesSearch}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                />
              </FormControl>

              {/* Cities List */}
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {loadingCities ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Loading cities...</Typography>
                  </Box>
                ) : filteredCities.length > 0 ? (
                  <>
                    <MenuItem value="" onClick={() => handleCitySelect('')}>
                      <em>None</em>
                    </MenuItem>
                    {filteredCities.map((city) => (
                      <MenuItem
                        key={city.id}
                        value={city.id}
                        onClick={() => handleCitySelect(city.id)}
                      >
                        {city.name}
                      </MenuItem>
                    ))}
                  </>
                ) : (
                  <Typography sx={{ color: 'text.secondary', textAlign: 'center', p: 3 }}>
                    {citiesSearch
                      ? 'No cities found'
                      : form.state
                      ? 'No cities available for this state'
                      : 'Select a state first'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Popover>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            label="Description *"
            name="description"
            value={form.description}
            onChange={handleInput}
            placeholder="Describe the property..."
            disabled={loadingProperty}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth disabled={loadingProperty || loadingStatus}>
            <InputLabel>Property Status *</InputLabel>
            <Select
              name="status"
              value={form.status}
              label="Property Status *"
              onChange={handleInput}
            >
              <MenuItem value="">
                <em>Select Status</em>
              </MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status.id} value={status.name}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth disabled={loadingProperty}>
            <InputLabel>Type *</InputLabel>
            <Select name="type" value={form.type} label="Type *" onChange={handleInput}>
              <MenuItem value="">
                <em>Select Type</em>
              </MenuItem>
              {typeOptions.map((type) => (
                <MenuItem key={type.id} value={type.name}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Price *"
            name="price"
            value={form.price}
            onChange={handleInput}
            onKeyDown={preventNegativeInput}
            type="number"
            fullWidth
            disabled={loadingProperty}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth disabled={loadingProperty}>
            <InputLabel>Area Unit *</InputLabel>
            <Select
              name="areaUnit"
              value={form.areaUnit}
              label="Area Unit *"
              onChange={handleInput}
            >
              <MenuItem value="">
                <em>Select Area Unit</em>
              </MenuItem>
              {areaUnitOptions.map((unit) => (
                <MenuItem key={unit.id} value={unit.name}>
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Build up Area *"
            name="buildUpArea"
            value={form.buildUpArea}
            onChange={handleInput}
            fullWidth
            type="number"
            disabled={loadingProperty}
            onKeyDown={preventNegativeInput}
          />
        </Grid>

        <Grid item xs={12} md={4} alignItems="center" display="flex">
          <Tooltip title="Price per area unit" placement="top" arrow>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    form.is_area_unit === 1 ||
                    form.is_area_unit === true ||
                    form.is_area_unit === '1'
                  }
                  onChange={handleInput}
                  name="is_area_unit"
                  disabled={loadingProperty}
                />
              }
              label={`Price per ${getSelectedAreaUnitName()}`}
            />
          </Tooltip>
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Build Year *"
              views={['year']}
              value={form.buildYear ? new Date(form.buildYear, 0, 1) : null}
              onChange={(newValue) => {
                handleInput({
                  target: {
                    name: 'buildYear',
                    value: newValue ? newValue.getFullYear().toString() : ''
                  }
                });
              }}
              disabled={loadingProperty}
              otProps={{
                textField: {
                  fullWidth: true,
                  InputLabelProps: { shrink: true }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            label="Bedrooms *"
            name="bedrooms"
            type="number"
            value={form.bedrooms}
            onChange={handleInput}
            fullWidth
            disabled={loadingProperty}
            onKeyDown={preventNegativeInput}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            label="Bathrooms *"
            name="bathrooms"
            type="number"
            value={form.bathrooms}
            onChange={handleInput}
            fullWidth
            disabled={loadingProperty}
            onKeyDown={preventNegativeInput}
          />
        </Grid>

        <Grid item xs={12} md={6} alignItems="center" display="flex">
          <FormControlLabel
            control={
              <Switch
                checked={form.is_garage}
                onChange={handleInput}
                name="is_garage"
                disabled={loadingProperty}
              />
            }
            label="Add Garage"
          />
        </Grid>

        {form.is_garage && (
          <Grid item xs={12} md={6}>
            <TextField
              label="No: of Cars *"
              name="garageSize"
              type="number"
              value={form?.garageSize}
              onChange={handleInput}
              onKeyDown={limitMaxTen}
              fullWidth
              disabled={loadingProperty}
            />
          </Grid>
        )}

        <Grid item xs={12} md={4} alignItems="center" display="flex">
          <FormControlLabel
            control={
              <Switch
                checked={form.is_featured}
                onChange={handleInput}
                name="is_featured"
                disabled={loadingProperty}
              />
            }
            label="Is Featured"
          />
        </Grid>

        <Grid item xs={12} md={4} alignItems="center" display="flex">
          <FormControlLabel
            control={
              <Switch
                checked={form.show_nearby}
                onChange={handleInput}
                name="show_nearby"
                disabled={loadingProperty}
              />
            }
            label="Show Nearby Events"
          />
        </Grid>

        <Grid item xs={12} md={4} alignItems="center" display="flex">
          <FormControlLabel
            control={
              <Switch
                checked={form.video_chat}
                onChange={handleInput}
                name="video_chat"
                disabled={loadingProperty}
              />
            }
            label="Allow Video Tours"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicDetailTab;
